import { CPLAT_API_ROUTES, useSuspenseFetchs } from '@/api';
import { useResponsive } from '@/hooks/useResponsive';
import { ROUTES } from '@/routes';
import { cn } from '@/utils/tailwind';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import WarningYellowIcon from '@/assets/icons/WarningYellowIcon.svg?react';
import usePostMessage from '@/hooks/usePostMessage';
import { useLog } from '@/hooks/V3/useLog';
import { useAppSelector } from '@/redux/store';
import { deviceUuidState } from '@/store/atom';
import { useRecoilValue } from 'recoil';
import MarketIcon from './MarketIcon.svg?react';
import { useRef, useEffect } from 'react';

export type selectedStoreKeyType = {
  MarketId?: string;
  StoreId?: string;
};

interface IMarketSidebar {
  storeList: Array<Store>;
  selectedStoreKey: selectedStoreKeyType;
  setSelectedStoreKey: React.Dispatch<React.SetStateAction<selectedStoreKeyType>>;
  enableTotal: boolean;
}

const MarketSidebar = ({
  storeList,
  selectedStoreKey,
  setSelectedStoreKey,
  enableTotal,
}: IMarketSidebar) => {
  const navigate = useNavigate();
  const { navigateParams } = usePostMessage();
  const user = useAppSelector((state) => state.user);
  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [{ data: marketInfoRes }] = useSuspenseFetchs([
    { url: CPLAT_API_ROUTES.getMarketModel, option: { method: 'POST' } },
  ]);
  const { data: marketInfoList } = marketInfoRes as AxiosResponse<MarketInfo[]>;
  const addMarket = marketInfoList.at(-1);

  const isSelectedStore = (item: Store) => {
    return (
      selectedStoreKey.MarketId === String(item.MarketId) &&
      selectedStoreKey.StoreId === item.StoreId
    );
  };

  const HasFurtherAction = storeList.find((item) => item.HasInquiry || item.HasFurtherAction);

  // 선택된 항목을 중앙으로 스크롤
  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const key =
      selectedStoreKey.StoreId === ''
        ? 'total'
        : `${selectedStoreKey.MarketId}-${selectedStoreKey.StoreId}`;

    const selectedElement = itemRefs.current.get(key);

    if (selectedElement && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const elementLeft = selectedElement.offsetLeft;
      const elementWidth = selectedElement.offsetWidth;
      const containerWidth = container.offsetWidth;

      // 요소를 중앙으로
      const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [selectedStoreKey, storeList]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex gap-[6px] overflow-x-auto whitespace-nowrap bg-white px-[20px] pb-[12px] scrollbar-hide"
    >
      {enableTotal && (
        <div
          ref={(el) => el && itemRefs.current.set('total', el)}
          className={cn(
            'flex min-w-fit items-center rounded-[20px] border-[1px] border-gr-50 p-[8px] pr-[14px]',
            selectedStoreKey.StoreId === ''
              ? 'bg-gr-800  text-white Body4S14'
              : 'text-gr-700 Body5M14',
          )}
          onClick={() => {
            setSelectedStoreKey({
              MarketId: '0',
              StoreId: '',
            });
            setUserEventHistory({
              eventName: 'select_market',
              eventValue: '전체',
              deviceUuid,
            });
          }}
        >
          <MarketIcon />
          <span className="ml-[6px]">전체</span>
          {HasFurtherAction && (
            <span className="ml-[2px] h-[4px] w-[4px] self-start rounded-full bg-red-300"></span>
          )}
        </div>
      )}

      {storeList.map((item: Store) => {
        const key = `${item.MarketId}-${item.StoreId}`;
        return (
          <div
            key={key}
            ref={(el) => el && itemRefs.current.set(key, el)}
            className={cn(
              'flex min-w-fit items-center rounded-[20px] border-[1px] border-gr-50 p-[8px]',
              isSelectedStore(item)
                ? 'border-gr-800  bg-gr-800 text-white Body4S14'
                : 'text-gr-700 Body5M14',
              item.IsReleasedStatus ? 'pr-[8px]' : 'pr-[14px]',
            )}
            onClick={() => {
              setSelectedStoreKey({
                MarketId: String(item.MarketId),
                StoreId: item.StoreId,
              });
              setUserEventHistory({
                eventName: 'select_market',
                eventValue: item.StoreId,
                deviceUuid,
              });
            }}
          >
            <img src={item.MarketIconUrl} alt="icon" className="h-[20px] w-[20px]" />
            <span className="ml-[6px]">{item.StoreName}</span>
            {item.IsReleasedStatus ? (
              <span className="ml-[6px]">
                <WarningYellowIcon />
              </span>
            ) : (
              <>
                {(item.HasFurtherAction || item.HasInquiry) && (
                  <span className="ml-[2px] h-[4px] w-[4px] self-start rounded-full bg-red-300"></span>
                )}
              </>
            )}
          </div>
        );
      })}
      <div
        className={cn(
          'flex min-w-fit items-center gap-[6px] rounded-[20px] border-[1px] border-gr-50 p-[8px] pr-[14px] text-gr-700 Body5M14',
        )}
        onClick={() => navigateParams('MarketInfo')}
      >
        <MarketIcon />
        <span>다른 마켓 연동하기</span>
      </div>
    </div>
  );
};

export default MarketSidebar;
